<template>
  <Modal
    elem-id="modal_support_ticket"
    :show="isVisible"
    :title="t('Abrir Chamado de Suporte')"
    :save-fn="submitTicket"
    :save-label="t('Enviar Chamado')"
    save-icon="pi pi-send"
    :save-disabled-fn="() => !isFormValid || isLoading"
    :close-label="t('Cancelar')"
    size="medium"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isVisible">
      <div class="support-ticket-form">
        <!-- Title Field (using inputs/InputText.vue) -->
        <input-text
          v-model="formData.name"
          :label="t('Assunto')"
          :placeholder="t('Descreva brevemente o problema')"
          :required="true"
          :disabled="isLoading"
          :validation="() => titleValid"
          elem-id="support_name"
          data-testid="support-ticket-name"
        />

        <!-- Description Field (using inputs/InputRichText.vue) -->
        <InputRichText
          v-model="formData.description"
          :label="t('Descrição')"
          :placeholder="t('Descreva o problema em detalhes. Você pode colar imagens.')"
          :show-toolbar="true"
          :ng-required="true"
          min-height="200px"
          :disabled="isLoading"
          data-testid="support-ticket-description"
        />

        <!-- Context Info (read-only) -->
        <div class="context-info">
          <div class="context-label">
            <i class="pi pi-info-circle"></i>
            {{ t('Informações incluídas automaticamente') }}:
          </div>
          <div class="context-details">
            <span><strong>{{ t('Usuário') }}:</strong> {{ userName }}</span>
            <span><strong>{{ t('Página') }}:</strong> {{ truncatedPageUri }}</span>
          </div>
        </div>

        <!-- Loading indicator -->
        <div v-if="isLoading" class="loading-overlay">
          <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
          <span>{{ t('Enviando chamado...') }}</span>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useOdooTicket } from '../../composables/useOdooTicket';
import { permissionsService } from '../../services/permissionsService';
import { log } from '../../utils/DebugLogger';

interface SupportTicketFormData {
  name: string;
  description: string;
  page_uri: string;
}

interface CreateTicketResult {
  success: boolean;
  ticketId?: number | string;
  error?: string;
}

const { t } = useI18n();
const { createTicket, getCurrentPageUri, isLoading } = useOdooTicket();

const isVisible = ref<boolean>(false);
const formData = ref<SupportTicketFormData>({
  name: '',
  description: '',
  page_uri: ''
});

const userName = computed<string>(() => {
  const userData = permissionsService.getUser() as { name?: string; email?: string } | null;
  return userData?.name || userData?.email || 'Usuário';
});

const truncatedPageUri = computed<string>(() => {
  const uri = formData.value.page_uri;
  if (uri.length > 50) {
    return uri.substring(0, 50) + '...';
  }
  return uri;
});

const titleValid = computed<boolean>(() => {
  return formData.value.name.trim().length > 0;
});

const descriptionValid = computed<boolean>(() => {
  if (!formData.value.description) return false;
  const stripped = formData.value.description.replace(/<[^>]*>/g, '').trim();
  return stripped.length > 0;
});

const isFormValid = computed<boolean>(() => {
  const result = titleValid.value && descriptionValid.value;
  log('SupportTicketModal', 'isFormValid', 'Computing isFormValid', {
    titleValid: titleValid.value,
    descriptionValid: descriptionValid.value,
    isFormValid: result,
    isLoading: isLoading.value
  });
  return result;
});

watch(() => formData.value.name, (newVal: string) => {
  log('SupportTicketModal', 'watch:name', 'Name changed', {
    name: newVal,
    trimmedLength: newVal?.trim().length,
    titleValid: titleValid.value
  });
}, { immediate: true });

watch(() => formData.value.description, (newVal: string) => {
  const stripped = newVal ? newVal.replace(/<[^>]*>/g, '').trim() : '';
  log('SupportTicketModal', 'watch:description', 'Description changed', {
    descriptionLength: newVal?.length,
    strippedLength: stripped.length,
    descriptionValid: descriptionValid.value
  });
}, { immediate: true });

const resetForm = (): void => {
  formData.value = {
    name: '',
    description: '',
    page_uri: getCurrentPageUri()
  };
};

const openModal = (): void => {
  log('SupportTicketModal', 'openModal', 'Opening modal');
  formData.value.page_uri = getCurrentPageUri();
  isVisible.value = true;
};

const closeModal = (): void => {
  log('SupportTicketModal', 'closeModal', 'Closing modal');
  isVisible.value = false;
  resetForm();
};

const handleModalOpen = (data?: unknown): void => {
  log('SupportTicketModal', 'handleModalOpen', 'Modal open event received', data);
  openModal();
};

const submitTicket = async (): Promise<void> => {
  log('SupportTicketModal', 'submitTicket', 'Submitting ticket', formData.value);

  const result = (await createTicket({
    name: formData.value.name,
    description: formData.value.description,
    page_uri: formData.value.page_uri
  })) as CreateTicketResult;

  if (result.success) {
    log('SupportTicketModal', 'submitTicket', 'Ticket created successfully', result);

    eventBus.emit('globalMessage.success', {
      message: t('Chamado criado com sucesso') + (result.ticketId ? ` (#${result.ticketId})` : ''),
      duration: 5000
    });

    closeModal();
  } else {
    log('SupportTicketModal', 'submitTicket', 'Failed to create ticket', result);

    eventBus.emit('globalMessage.error', {
      message: result.error || t('Erro ao criar chamado'),
      duration: 5000
    });
  }
};

const handleKeyboardSubmit = (event: KeyboardEvent): void => {
  if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
    log('SupportTicketModal', 'handleKeyboardSubmit', 'Cmd/Ctrl+Enter pressed', {
      isVisible: isVisible.value,
      isFormValid: isFormValid.value,
      isLoading: isLoading.value
    });

    if (isVisible.value && isFormValid.value && !isLoading.value) {
      event.preventDefault();
      void submitTicket();
    }
  }
};

// Event listeners
onMounted(() => {
  log('SupportTicketModal', 'onMounted', 'Component mounted');

  // Standard modal events
  eventBus.on('modal.support-ticket.open', handleModalOpen);
  eventBus.on('modal.support-ticket.close', closeModal);

  // Legacy/alternative event names
  eventBus.on('support-ticket.open', handleModalOpen);

  // Add keyboard shortcut listener for Cmd/Ctrl + Enter
  document.addEventListener('keydown', handleKeyboardSubmit);
});

onBeforeUnmount(() => {
  log('SupportTicketModal', 'onBeforeUnmount', 'Component unmounting');

  eventBus.off('modal.support-ticket.open', handleModalOpen);
  eventBus.off('modal.support-ticket.close', closeModal);
  eventBus.off('support-ticket.open', handleModalOpen);

  // Remove keyboard shortcut listener
  document.removeEventListener('keydown', handleKeyboardSubmit);
});
</script>

<style scoped>
.support-ticket-form {
  position: relative;
}

.context-info {
  background-color: var(--surface-100);
  border-radius: 8px;
  padding: 12px;
  font-size: 0.875rem;
  margin-top: 1rem;
}

.context-label {
  color: var(--text-color-secondary);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.context-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: var(--text-color);
}

.context-details span {
  word-break: break-all;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  z-index: 10;
}
</style>
